import {
    fetchBaseQuery,
    type BaseQueryApi,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
    type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { accessToken } from '../../configuration/tokenHandling/accessToken';
import { backendUrls } from './config';

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];

export interface JsonObject {
    [key: string]: JsonValue;
}

export type ApiServiceName = 'userAdmin' | 'accounts' | 'tags' | 'auth';

export interface ApiProblemBody {
    code?: string;
    detail?: string;
}

export interface ApiError {
    status: number;
    message: string;
    body: ApiProblemBody;
}

export const isApiError = (error: object | null | undefined): error is ApiError =>
    Boolean(error && 'status' in error && 'message' in error && 'body' in error);

type QueryParamValue = string | number | boolean | null | undefined;

export interface ServiceQueryArgs {
    service?: ApiServiceName;
    path?: string;
    absoluteUrl?: string;
    method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
    body?: object;
    headers?: Record<string, string>;
    params?: Record<string, QueryParamValue>;
    tokenOverride?: string;
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: '',
    responseHandler: async response => {
        const text = await response.text();
        if (!text) {
            return null;
        }

        try {
            return JSON.parse(text) as JsonValue;
        } catch {
            return text;
        }
    },
});

const backendUrlByService: Record<ApiServiceName, string | undefined> = {
    userAdmin: backendUrls.USER_ADMINISTRATION,
    accounts: backendUrls.ACCOUNTS_SERVICE,
    tags: backendUrls.TAGS_SERVICE,
    auth: backendUrls.AUTH_SERVER,
};

const ensureSlashPrefixedPath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

const sanitizeParams = (params?: Record<string, QueryParamValue>) => {
    if (!params) {
        return undefined;
    }

    const filteredEntries = Object.entries(params).filter((entry): entry is [string, string | number | boolean] => {
        const value = entry[1];
        return value !== undefined && value !== null;
    });

    return Object.fromEntries(filteredEntries);
};

const resolveUrl = (args: ServiceQueryArgs) => {
    if (args.absoluteUrl) {
        return args.absoluteUrl;
    }

    if (!args.service) {
        throw new Error('service is required when absoluteUrl is not provided');
    }

    const serviceBaseUrl = backendUrlByService[args.service];
    if (!serviceBaseUrl) {
        throw new Error(`Base URL is missing for service "${args.service}"`);
    }
    if (!args.path) {
        throw new Error(`path is required for service "${args.service}"`);
    }

    return `${serviceBaseUrl}${ensureSlashPrefixedPath(args.path)}`;
};

const buildHeaders = (args: ServiceQueryArgs) => {
    const headers = new Headers(args.headers);
    const bearerToken = args.tokenOverride ?? accessToken.getAccessToken();

    if (bearerToken) {
        headers.set('Authorization', `Bearer ${bearerToken}`);
    }

    if (args.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    return headers;
};

const buildFetchArgs = (args: ServiceQueryArgs): FetchArgs => ({
    url: resolveUrl(args),
    method: args.method ?? 'GET',
    body: args.body,
    headers: buildHeaders(args),
    params: sanitizeParams(args.params),
});

const getProblemBody = (data: FetchBaseQueryError['data']): ApiProblemBody => {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const candidate = data as Record<string, JsonValue>;
        return {
            code: typeof candidate.code === 'string' ? candidate.code : undefined,
            detail: typeof candidate.detail === 'string' ? candidate.detail : undefined,
        };
    }

    return {};
};

const getErrorMessage = (status: number) => {
    switch (status) {
        case 401:
            return 'UNAUTHENTICATED';
        case 403:
            return 'ACCESS_DENIED';
        default:
            return 'Backend error';
    }
};

const toApiError = (error: FetchBaseQueryError): ApiError => {
    if (typeof error.status === 'number') {
        const body = getProblemBody(error.data);
        return {
            status: error.status,
            message: getErrorMessage(error.status),
            body,
        };
    }

    switch (error.status) {
        case 'FETCH_ERROR':
            return {
                status: 0,
                message: error.error,
                body: {},
            };
        case 'PARSING_ERROR':
            return {
                status: error.originalStatus,
                message: error.error,
                body: {},
            };
        case 'TIMEOUT_ERROR':
            return {
                status: 0,
                message: error.error,
                body: {},
            };
        case 'CUSTOM_ERROR':
            return {
                status: 0,
                message: error.error,
                body: {},
            };
    }
};

export const createApiError = (status: number, message: string, body: ApiProblemBody = {}): ApiError => ({
    status,
    message,
    body,
});

export const serviceBaseQuery: BaseQueryFn<
    ServiceQueryArgs,
    JsonValue | null,
    ApiError,
    object,
    FetchBaseQueryMeta
> = async (args: ServiceQueryArgs, api: BaseQueryApi, extraOptions: NonNullable<unknown>) => {
    const result = await rawBaseQuery(buildFetchArgs(args), api, extraOptions);

    if (result.error) {
        return {
            error: toApiError(result.error),
            meta: result.meta,
        };
    }

    return {
        data: result.data as JsonValue | null,
        meta: result.meta,
    };
};
